import { getRecordsWithOperationsApplied, op, Operation } from "libs/transaction";
import * as ops from "libs/operations";
import { DraftAttachmentDoc, getMapRecords, getPointer } from "libs/schema";
import { createAction } from "./action";
import { InvalidActionStateError } from "./utils/errors";
import { DraftRecipient, EditorMention, getToRecipients } from "./draft";
import { undeleteOperation } from "./utils/operations";
import { authorizeUserCanUpdateMessage } from "./authorizers/messageAuthorizers";
import { authorizeUserCanUpdateDraftEdit } from "./authorizers/draftAuthorizers";

/* -------------------------------------------------------------------------------------------------
 *  createEditDraft
 * -------------------------------------------------------------------------------------------------
 */

type CreateEditDraftProps = {
  messageId: string;
  ownerOrganizationId: string;
  threadId: string;
  to?: DraftRecipient[];
  cc?: DraftRecipient[];
  bcc?: DraftRecipient[];
  bodyHTML?: string;
  userMentions?: EditorMention[];
  groupMentions?: EditorMention[];
  attachments?: DraftAttachmentDoc[];
};

export const createEditDraft = createAction<CreateEditDraftProps>("draftEdit.createEditDraft", {
  operations: async (props, context) => {
    const [message] = await context.environment.loader.getRecord("message", props.messageId);

    if (!message) {
      throw new InvalidActionStateError("[createEditDraft] Message not found", {
        errorData: { id: props.messageId },
      });
    }

    if (message.deleted_at) {
      throw new InvalidActionStateError("[createEditDraft] Message was already deleted", {
        errorData: {
          id: props.messageId,
          deletedAt: message.deleted_at,
          deletedByUserId: message.deleted_by_user_id,
        },
      });
    }

    return ops.draftEdit.createEditDraft({
      messageId: props.messageId,
      userId: context.currentUserId,
      ownerOrganizationId: props.ownerOrganizationId,
      threadId: props.threadId,
      attachments: props.attachments,
      bodyHTML: props.bodyHTML,
      to: getToRecipients(props),
    });
  },
  authorize: async (props, context) => {
    await authorizeUserCanUpdateMessage({ messageId: props.messageId, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  setDraftEdit
 * -------------------------------------------------------------------------------------------------
 */

type SetDraftEditProps = {
  draftId: string;
  ownerOrganizationId: string;
  threadId: string;
  bodyHTML?: string;
  to?: DraftRecipient[];
  cc?: DraftRecipient[];
  bcc?: DraftRecipient[];
  userMentions?: EditorMention[];
  groupMentions?: EditorMention[];
  attachments?: DraftAttachmentDoc[];
};

export const setDraftEdit = createAction<SetDraftEditProps>("draftEdit.setDraftEdit", {
  operations: async (props, context) => {
    const [draftEdit] = await context.environment.loader.getRecord("draft_edit", props.draftId);

    if (draftEdit?.deleted_at) {
      throw new InvalidActionStateError("[setDraftEdit] DraftEdit was already deleted", {
        errorData: {
          id: draftEdit.id,
          deletedAt: draftEdit.deleted_at,
          deletedByUserId: draftEdit.deleted_by_user_id,
        },
      });
    }

    return ops.draftEdit.setDraftEdit({
      draftId: props.draftId,
      userId: context.currentUserId,
      ownerOrganizationId: props.ownerOrganizationId,
      threadId: props.threadId,
      attachments: props.attachments,
      bodyHTML: props.bodyHTML,
      to: getToRecipients(props),
    });
  },
  authorize: async (props, context) => {
    await authorizeUserCanUpdateDraftEdit({ draftEditId: props.draftId, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  commitDraftEdits
 * -------------------------------------------------------------------------------------------------
 */

type CommitDraftEditsProps = SetDraftEditProps & {
  shouldResendNotifications?: boolean;
};

export const commitDraftEdits = createAction<CommitDraftEditsProps>("draftEdit.commitDraftEdits", {
  operations: async (props, context) => {
    const { loader, logger } = context.environment;

    const operations: Operation[] = [];

    // Check locally to make sure the draft exists
    const [existingDraft] = await loader.getRecord("draft_edit", props.draftId);

    if (!existingDraft) {
      throw new InvalidActionStateError("[commitDraftEdits] DraftEdit not found", { errorData: { id: props.draftId } });
    }

    operations.push(
      ...ops.draftEdit.setDraftEdit({
        draftId: props.draftId,
        userId: context.currentUserId,
        ownerOrganizationId: props.ownerOrganizationId,
        threadId: props.threadId,
        attachments: props.attachments,
        bodyHTML: props.bodyHTML,
        to: getToRecipients(props),
      }),
    );

    const afterRecordMap = await getRecordsWithOperationsApplied(loader, {
      operations,
      authorId: context.currentUserId,
    });

    const [draftEdit] = getMapRecords(afterRecordMap, "draft_edit");

    if (!draftEdit) {
      throw new InvalidActionStateError(`[commitDraftEdits] DraftEdit not found after applying edit operations`, {
        errorData: { id: props.draftId },
      });
    }

    logger.notice({ draftId: draftEdit.id }, `[commitDraftEdits] applying edits ...`);

    const [message] = await loader.getRecord("message", draftEdit.id);

    if (!message) {
      throw new InvalidActionStateError(`[commitDraftEdits] Message not found`, {
        errorData: { id: draftEdit.id },
      });
    }

    operations.push(
      ...ops.draftEdit.applyEditsToMessage({
        draft: draftEdit,
        message,
        shouldResendNotifications: !!props.shouldResendNotifications,
      }),
    );

    // Delete the draft now that it's been sent.
    operations.push(op.delete("draft_edit", draftEdit));

    return operations;
  },
  authorize: async (props, context) => {
    await Promise.all([
      authorizeUserCanUpdateDraftEdit({ draftEditId: props.draftId, context }),
      authorizeUserCanUpdateMessage({ messageId: props.draftId, context }),
    ]);
  },
});

/* -------------------------------------------------------------------------------------------------
 *  deleteDraftEdit
 * -------------------------------------------------------------------------------------------------
 */

type DeleteDraftEditProps = {
  draftId: string;
};

export const deleteDraftEdit = createAction<DeleteDraftEditProps>("draftEdit.deleteDraftEdit", {
  operations: async (props) => {
    return op.delete("draft_edit", props.draftId);
  },
  authorize: async (props, context) => {
    await authorizeUserCanUpdateDraftEdit({ draftEditId: props.draftId, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  unDeleteDraftEdit
 * -------------------------------------------------------------------------------------------------
 */

type UnDeleteDraftEditProps = {
  draftId: string;
};

export const unDeleteDraftEdit = createAction<UnDeleteDraftEditProps>("draftEdit.unDeleteDraftEdit", {
  operations: async (props) => {
    return undeleteOperation(getPointer("draft_edit", props.draftId));
  },
  authorize: async (props, context) => {
    await authorizeUserCanUpdateDraftEdit({ draftEditId: props.draftId, context });
  },
});

export const actions = {
  createEditDraft,
  setDraftEdit,
  commitDraftEdits,
  deleteDraftEdit,
  unDeleteDraftEdit,
};
